/**
 * Scheduling Control Page
 * Provides controls for backward/forward scheduling, hybrid planning,
 * capacity checks, sequence optimization, and reschedule preview.
 */

import { useState, useCallback } from 'react';
import { Layout } from '../components/layout';
import {
  useBackwardScheduling,
  useForwardScheduling,
  useHybridPlanning,
  useCapacityCheck,
  useSequenceOptimize,
  useReschedulePreview,
  useSchedulingCacheStatus,
  useInvalidateSchedulingCache,
  getConflictSeverityColor,
  getConflictSeverityLabel,
  getSchedulingStatusColor,
  formatComputationTime,
} from '../hooks';
import type {
  SchedulingRequest,
  HybridPlanningRequest,
  SchedulingResult,
  HybridPlanningResult,
  SchedulingConflict,
  ScheduledOperation,
  AIOptimizationFocus,
} from '../types';
import {
  Play,
  Rewind,
  FastForward,
  Zap,
  BarChart3,
  ListOrdered,
  Clock,
  AlertTriangle,
  CheckCircle2,
  Loader2,
  RefreshCw,
  ChevronDown,
  ChevronUp,
  Database,
  Shield,
  Target,
  Settings2,
} from 'lucide-react';
import clsx from 'clsx';

// ============ Types ============

type SchedulingMode = 'backward' | 'forward' | 'hybrid';

// ============ Result Panel ============

function SchedulingResultPanel({
  result,
  label,
}: {
  result: SchedulingResult;
  label: string;
}) {
  const [showOps, setShowOps] = useState(false);
  const [showConflicts, setShowConflicts] = useState(
    (result?.conflicts?.length ?? 0) > 0
  );

  // Guard: if result is missing or malformed, show a fallback
  if (!result || !result.operations) {
    return (
      <div className="bg-white rounded-xl border border-amber-200 p-6">
        <h3 className="text-lg font-semibold text-amber-700">{label}</h3>
        <p className="text-sm text-amber-600 mt-2">
          No result data available. Check backend logs for details.
        </p>
      </div>
    );
  }

  return (
    <div className="bg-white rounded-xl border border-gray-200 overflow-hidden">
      {/* Header */}
      <div className="px-6 py-4 border-b border-gray-100 flex items-center justify-between">
        <div className="flex items-center gap-3">
          <div
            className={clsx(
              'w-3 h-3 rounded-full',
              result.has_errors
                ? 'bg-red-500'
                : result.infeasible_operations > 0
                ? 'bg-amber-500'
                : 'bg-green-500'
            )}
          />
          <h3 className="text-lg font-semibold text-gray-800">
            {label}
          </h3>
        </div>
        <span className="text-xs text-gray-400">
          {formatComputationTime(result.computation_time_ms)}
        </span>
      </div>

      {/* Stats */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4 p-6">
        <div>
          <p className="text-xs text-gray-500">Scheduled</p>
          <p className="text-xl font-bold text-green-600">
            {result.scheduled_operations}
            <span className="text-sm text-gray-400 font-normal">
              /{result.total_operations}
            </span>
          </p>
        </div>
        <div>
          <p className="text-xs text-gray-500">Success Rate</p>
          <p
            className={clsx(
              'text-xl font-bold',
              getSchedulingStatusColor(
                result.success_rate >= 90
                  ? 'success'
                  : result.success_rate >= 50
                  ? 'partial'
                  : 'failed'
              )
            )}
          >
            {Number(result.success_rate ?? 0).toFixed(1)}%
          </p>
        </div>
        <div>
          <p className="text-xs text-gray-500">Not Schedulable</p>
          <p
            className={clsx(
              'text-xl font-bold',
              result.infeasible_operations > 0
                ? 'text-red-600'
                : 'text-gray-400'
            )}
          >
            {result.infeasible_operations}
          </p>
        </div>
        <div>
          <p className="text-xs text-gray-500">Conflicts</p>
          <p
            className={clsx(
              'text-xl font-bold',
              result.conflicts.length > 0
                ? 'text-amber-600'
                : 'text-gray-400'
            )}
          >
            {result.conflicts.length}
          </p>
        </div>
      </div>

      {/* Conflicts */}
      {result.conflicts.length > 0 && (
        <div className="border-t border-gray-100">
          <button
            onClick={() => setShowConflicts(!showConflicts)}
            className="w-full px-6 py-3 flex items-center justify-between text-sm font-medium text-gray-600 hover:bg-gray-50"
          >
            <span>
              Conflicts ({result.conflicts.length})
            </span>
            {showConflicts ? (
              <ChevronUp className="w-4 h-4" />
            ) : (
              <ChevronDown className="w-4 h-4" />
            )}
          </button>
          {showConflicts && (
            <div className="px-6 pb-4 space-y-2">
              {result.conflicts.slice(0, 20).map((conflict, idx) => (
                <ConflictRow key={idx} conflict={conflict} />
              ))}
              {result.conflicts.length > 20 && (
                <p className="text-xs text-gray-400 text-center pt-2">
                  +{result.conflicts.length - 20} more conflicts
                </p>
              )}
            </div>
          )}
        </div>
      )}

      {/* Operations */}
      <div className="border-t border-gray-100">
        <button
          onClick={() => setShowOps(!showOps)}
          className="w-full px-6 py-3 flex items-center justify-between text-sm font-medium text-gray-600 hover:bg-gray-50"
        >
          <span>
            Operationen ({result.operations.length})
          </span>
          {showOps ? (
            <ChevronUp className="w-4 h-4" />
          ) : (
            <ChevronDown className="w-4 h-4" />
          )}
        </button>
        {showOps && (
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead>
                <tr className="bg-gray-50/50 border-b border-gray-100">
                  <th className="py-2 px-4 text-left text-xs font-semibold text-gray-500">
                    Order
                  </th>
                  <th className="py-2 px-4 text-left text-xs font-semibold text-gray-500">
                    Seq
                  </th>
                  <th className="py-2 px-4 text-left text-xs font-semibold text-gray-500">
                    Work Center
                  </th>
                  <th className="py-2 px-4 text-left text-xs font-semibold text-gray-500">
                    Start
                  </th>
                  <th className="py-2 px-4 text-left text-xs font-semibold text-gray-500">
                    Ende
                  </th>
                  <th className="py-2 px-4 text-right text-xs font-semibold text-gray-500">
                    Duration (min)
                  </th>
                </tr>
              </thead>
              <tbody>
                {result.operations.slice(0, 50).map((op) => (
                  <OperationRow key={op.operation_id} op={op} />
                ))}
              </tbody>
            </table>
            {result.operations.length > 50 && (
              <p className="text-xs text-gray-400 text-center py-3">
                +{result.operations.length - 50} more operations
              </p>
            )}
          </div>
        )}
      </div>
    </div>
  );
}

// ============ Conflict Row ============

function ConflictRow({ conflict }: { conflict: SchedulingConflict }) {
  return (
    <div
      className={clsx(
        'flex items-start gap-3 px-3 py-2 rounded-lg border text-sm',
        getConflictSeverityColor(conflict.severity)
      )}
    >
      <AlertTriangle className="w-4 h-4 mt-0.5 flex-shrink-0" />
      <div className="min-w-0 flex-1">
        <div className="flex items-center gap-2">
          <span className="font-medium text-xs uppercase">
            {getConflictSeverityLabel(conflict.severity)}
          </span>
          <span className="text-xs opacity-70">
            {conflict.conflict_type}
          </span>
        </div>
        <p className="text-xs mt-0.5 opacity-90">{conflict.message}</p>
      </div>
    </div>
  );
}

// ============ Operation Row ============

function OperationRow({ op }: { op: ScheduledOperation }) {
  return (
    <tr
      className={clsx(
        'border-b border-gray-50',
        op.backward_infeasible && 'bg-red-50/30'
      )}
    >
      <td className="py-2 px-4 text-gray-800 font-medium">
        {op.erp_order_number}
      </td>
      <td className="py-2 px-4 text-gray-600">{op.sequence}</td>
      <td className="py-2 px-4 text-gray-600">
        {op.work_center_code}
      </td>
      <td className="py-2 px-4 text-gray-500 text-xs">
        {op.planned_start
          ? new Date(op.planned_start).toLocaleString('en-GB', {
              day: '2-digit',
              month: '2-digit',
              hour: '2-digit',
              minute: '2-digit',
            })
          : '—'}
      </td>
      <td className="py-2 px-4 text-gray-500 text-xs">
        {op.planned_end
          ? new Date(op.planned_end).toLocaleString('en-GB', {
              day: '2-digit',
              month: '2-digit',
              hour: '2-digit',
              minute: '2-digit',
            })
          : '—'}
      </td>
      <td className="py-2 px-4 text-right text-gray-600">
        {Number(op.total_duration_minutes ?? 0).toFixed(0)}
      </td>
    </tr>
  );
}

// ============ Main Page ============

export function SchedulingPage() {
  const [mode, setMode] = useState<SchedulingMode>('hybrid');
  const [optimizationFocus, setOptimizationFocus] =
    useState<AIOptimizationFocus>('balanced');
  const [includeCapacity, setIncludeCapacity] = useState(true);
  const [includeMaterial, setIncludeMaterial] = useState(true);
  const [includeSequence, setIncludeSequence] = useState(true);

  // Results
  const [schedulingResult, setSchedulingResult] =
    useState<SchedulingResult | null>(null);
  const [hybridResult, setHybridResult] =
    useState<HybridPlanningResult | null>(null);

  // Mutations
  const backward = useBackwardScheduling();
  const forward = useForwardScheduling();
  const hybrid = useHybridPlanning();
  const capacityCheck = useCapacityCheck();
  const sequenceOptimize = useSequenceOptimize();
  const cacheStatus = useSchedulingCacheStatus();
  const invalidateCache = useInvalidateSchedulingCache();

  const isRunning =
    backward.isPending ||
    forward.isPending ||
    hybrid.isPending;

  const handleRun = useCallback(() => {
    setSchedulingResult(null);
    setHybridResult(null);

    const baseRequest: SchedulingRequest = {
      include_material_constraints: includeMaterial,
    };

    if (mode === 'backward') {
      backward.mutate(baseRequest, {
        onSuccess: (result) => setSchedulingResult(result),
      });
    } else if (mode === 'forward') {
      forward.mutate(baseRequest, {
        onSuccess: (result) => setSchedulingResult(result),
      });
    } else {
      const hybridRequest: HybridPlanningRequest = {
        scheduling_direction: 'backward',
        include_sequence_optimization: includeSequence,
        include_material_constraints: includeMaterial,
        include_capacity_check: includeCapacity,
        ai_optimization_focus: optimizationFocus,
      };
      hybrid.mutate(hybridRequest, {
        onSuccess: (result) => setHybridResult(result),
      });
    }
  }, [
    mode,
    backward,
    forward,
    hybrid,
    includeMaterial,
    includeCapacity,
    includeSequence,
    optimizationFocus,
  ]);

  const focusOptions: { key: AIOptimizationFocus; label: string; icon: React.ElementType }[] = [
    { key: 'balanced', label: 'Balanced', icon: Target },
    { key: 'on_time_delivery', label: 'On-Time Delivery', icon: Clock },
    { key: 'utilization', label: 'Utilization', icon: BarChart3 },
    { key: 'minimize_setup', label: 'Min. Setup Time', icon: Settings2 },
  ];

  return (
    <Layout>
      <div className="p-6 space-y-6 max-w-7xl mx-auto">
        {/* Header */}
        <div className="flex items-center justify-between">
          <div>
            <h1 className="text-2xl font-bold text-gray-900 flex items-center gap-3">
              <Zap className="w-7 h-7 text-[#45bdd6]" />
              Scheduling Engine
            </h1>
            <p className="text-sm text-gray-500 mt-1">
              APS Production Planning — Forward, Backward, Hybrid
            </p>
          </div>
          {/* Cache Status */}
          <div className="flex items-center gap-3">
            {cacheStatus.data && (
              <div className="flex items-center gap-2 text-xs text-gray-500 bg-gray-50 px-3 py-2 rounded-lg">
                <Database className="w-3.5 h-3.5" />
                <span>
                  Cache:{' '}
                  {cacheStatus.data.is_valid ? (
                    <span className="text-green-600 font-medium">
                      Valid
                    </span>
                  ) : (
                    <span className="text-amber-600 font-medium">
                      Invalid
                    </span>
                  )}
                </span>
                <button
                  onClick={() => invalidateCache.mutate()}
                  disabled={invalidateCache.isPending}
                  className="text-gray-400 hover:text-gray-600"
                  title="Invalidate cache"
                >
                  <RefreshCw
                    className={clsx(
                      'w-3.5 h-3.5',
                      invalidateCache.isPending && 'animate-spin'
                    )}
                  />
                </button>
              </div>
            )}
          </div>
        </div>

        {/* Control Panel */}
        <div className="bg-white rounded-xl border border-gray-200 p-6 space-y-6">
          {/* Mode Selection */}
          <div>
            <label className="text-sm font-medium text-gray-700 mb-3 block">
              Planning Mode
            </label>
            <div className="flex gap-3">
              {[
                {
                  key: 'backward' as SchedulingMode,
                  label: 'Backward',
                  icon: Rewind,
                  desc: 'From due date back',
                },
                {
                  key: 'forward' as SchedulingMode,
                  label: 'Forward',
                  icon: FastForward,
                  desc: 'From earliest start',
                },
                {
                  key: 'hybrid' as SchedulingMode,
                  label: 'Hybrid (AI)',
                  icon: Zap,
                  desc: 'AI-optimized',
                },
              ].map((m) => (
                <button
                  key={m.key}
                  onClick={() => setMode(m.key)}
                  className={clsx(
                    'flex-1 flex items-center gap-3 p-4 rounded-lg border-2 transition-all',
                    mode === m.key
                      ? 'border-[#45bdd6] bg-[#45bdd6]/5'
                      : 'border-gray-200 hover:border-gray-300'
                  )}
                >
                  <m.icon
                    className={clsx(
                      'w-5 h-5',
                      mode === m.key
                        ? 'text-[#45bdd6]'
                        : 'text-gray-400'
                    )}
                  />
                  <div className="text-left">
                    <p
                      className={clsx(
                        'text-sm font-medium',
                        mode === m.key
                          ? 'text-[#45bdd6]'
                          : 'text-gray-700'
                      )}
                    >
                      {m.label}
                    </p>
                    <p className="text-xs text-gray-400">{m.desc}</p>
                  </div>
                </button>
              ))}
            </div>
          </div>

          {/* Hybrid Options */}
          {mode === 'hybrid' && (
            <div className="space-y-4">
              <label className="text-sm font-medium text-gray-700 block">
                AI Optimization Focus
              </label>
              <div className="flex gap-2">
                {focusOptions.map((opt) => (
                  <button
                    key={opt.key}
                    onClick={() => setOptimizationFocus(opt.key)}
                    className={clsx(
                      'flex items-center gap-2 px-4 py-2 rounded-lg text-sm transition-colors',
                      optimizationFocus === opt.key
                        ? 'bg-[#45bdd6] text-white'
                        : 'bg-gray-100 text-gray-600 hover:bg-gray-200'
                    )}
                  >
                    <opt.icon className="w-4 h-4" />
                    {opt.label}
                  </button>
                ))}
              </div>

              {/* Toggles */}
              <div className="flex gap-6">
                {[
                  {
                    id: 'capacity',
                    label: 'Capacity Check',
                    checked: includeCapacity,
                    onChange: setIncludeCapacity,
                  },
                  {
                    id: 'material',
                    label: 'Material Availability',
                    checked: includeMaterial,
                    onChange: setIncludeMaterial,
                  },
                  {
                    id: 'sequence',
                    label: 'Sequence Optimization',
                    checked: includeSequence,
                    onChange: setIncludeSequence,
                  },
                ].map((toggle) => (
                  <label
                    key={toggle.id}
                    className="flex items-center gap-2 text-sm text-gray-600 cursor-pointer"
                  >
                    <input
                      type="checkbox"
                      checked={toggle.checked}
                      onChange={(e) =>
                        toggle.onChange(e.target.checked)
                      }
                      className="w-4 h-4 text-[#45bdd6] border-gray-300 rounded focus:ring-[#45bdd6]"
                    />
                    {toggle.label}
                  </label>
                ))}
              </div>
            </div>
          )}

          {/* Run Button */}
          <button
            onClick={handleRun}
            disabled={isRunning}
            className={clsx(
              'w-full flex items-center justify-center gap-2 py-3 rounded-lg text-sm font-semibold transition-colors',
              isRunning
                ? 'bg-gray-100 text-gray-400 cursor-not-allowed'
                : 'bg-[#45bdd6] text-white hover:bg-[#3ba8c0]'
            )}
          >
            {isRunning ? (
              <>
                <Loader2 className="w-5 h-5 animate-spin" />
                Planning in progress...
              </>
            ) : (
              <>
                <Play className="w-5 h-5" />
                Start Planning
              </>
            )}
          </button>

          {/* Error */}
          {(backward.isError || forward.isError || hybrid.isError) && (
            <div className="bg-red-50 border border-red-200 rounded-lg p-3 flex items-start gap-2">
              <AlertTriangle className="w-4 h-4 text-red-500 mt-0.5" />
              <p className="text-sm text-red-700">
                Planning error. Check the backend and the data.
              </p>
            </div>
          )}
        </div>

        {/* Results */}
        {schedulingResult && (
          <SchedulingResultPanel
            result={schedulingResult}
            label={`${
              mode === 'backward' ? 'Backward' : 'Forward'
            } Planning Result`}
          />
        )}

        {hybridResult && (
          <div className="space-y-4">
            <SchedulingResultPanel
              result={hybridResult.scheduling_result}
              label="Hybrid Planning Result"
            />

            {/* AI Error/Recommendation Messages */}
            {hybridResult.ai_recommendations &&
              hybridResult.ai_recommendations.length > 0 && (
                <div className="bg-white rounded-xl border border-gray-200 p-4 space-y-2">
                  {hybridResult.ai_recommendations.map((rec: any, idx: number) => (
                    <div
                      key={idx}
                      className={clsx(
                        'flex items-start gap-2 text-sm px-3 py-2 rounded-lg',
                        rec.type === 'error'
                          ? 'bg-red-50 text-red-700'
                          : 'bg-blue-50 text-blue-700'
                      )}
                    >
                      <AlertTriangle className="w-4 h-4 mt-0.5 flex-shrink-0" />
                      <span>{rec.message || JSON.stringify(rec)}</span>
                    </div>
                  ))}
                </div>
              )}

            {/* Additional Hybrid Info */}
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
              <div className="bg-white rounded-xl border border-gray-200 p-4">
                <p className="text-xs text-gray-500 mb-1">
                  Optimization Focus
                </p>
                <p className="text-sm font-semibold text-gray-800">
                  {hybridResult.optimization_focus}
                </p>
              </div>
              <div className="bg-white rounded-xl border border-gray-200 p-4">
                <p className="text-xs text-gray-500 mb-1">
                  Total Computation Time
                </p>
                <p className="text-sm font-semibold text-gray-800">
                  {formatComputationTime(
                    hybridResult.total_computation_time_ms
                  )}
                </p>
              </div>
              <div className="bg-white rounded-xl border border-gray-200 p-4">
                <p className="text-xs text-gray-500 mb-1">
                  Material-Constraints
                </p>
                <p className="text-sm font-semibold text-gray-800">
                  {hybridResult.material_constraints_applied
                    ? 'Applied'
                    : 'Not Applied'}
                </p>
              </div>
            </div>

            {/* Sequence Results */}
            {hybridResult.sequence_result && (
              <div className="bg-white rounded-xl border border-gray-200 p-6">
                <h3 className="text-lg font-semibold text-gray-800 mb-3 flex items-center gap-2">
                  <ListOrdered className="w-5 h-5 text-[#45bdd6]" />
                  Sequence Optimization
                </h3>
                <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                  <div>
                    <p className="text-xs text-gray-500">
                      Setup Time Saved
                    </p>
                    <p className="text-xl font-bold text-green-600">
                      {Number(hybridResult.sequence_result.total_setup_saved_minutes ?? 0).toFixed(
                        0
                      )}{' '}
                      min
                    </p>
                  </div>
                  <div>
                    <p className="text-xs text-gray-500">
                      Work Center Optimized
                    </p>
                    <p className="text-xl font-bold text-gray-800">
                      {
                        hybridResult.sequence_result
                          .work_centers_improved
                      }
                      /
                      {
                        hybridResult.sequence_result
                          .work_centers_optimized
                      }
                    </p>
                  </div>
                  <div>
                    <p className="text-xs text-gray-500">
                      Strategy
                    </p>
                    <p className="text-sm font-medium text-gray-800">
                      {hybridResult.sequence_result.strategy_used}
                    </p>
                  </div>
                  <div>
                    <p className="text-xs text-gray-500">
                      Computation Time
                    </p>
                    <p className="text-sm font-medium text-gray-800">
                      {formatComputationTime(
                        hybridResult.sequence_result
                          .computation_time_ms
                      )}
                    </p>
                  </div>
                </div>
              </div>
            )}
          </div>
        )}
      </div>
    </Layout>
  );
}
